import { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import CompanyAddress from '../components/CompanyAddress';
import { submitLead } from '../utils/submitLead';
import {
  COMPANY_NAME,
  FOOTER_DISCLAIMER,
  SUPPORT_EMAIL,
  SUPPORT_HOURS,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TEL,
} from '../constants/site';

const channels = [
  {
    icon: 'phone',
    title: 'Call our support desk',
    value: SUPPORT_PHONE,
    href: `tel:${SUPPORT_PHONE_TEL}`,
    note: 'Fastest option. Most issues are resolved on the first call.',
  },
  {
    icon: 'mail',
    title: 'Email us',
    value: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
    note: 'Written replies within two business days, including weekends.',
  },
  {
    icon: 'clock',
    title: 'Opening hours',
    value: 'Open 24 hours, 7 days',
    note: SUPPORT_HOURS,
  },
  {
    icon: 'shield',
    title: 'Registered office',
    value: COMPANY_NAME,
    address: true,
    note: 'Send written notices and formal requests to this address.',
  },
];

export default function ContactPage() {
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setPending(true);

    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      await submitLead({
        name: String(data.get('name') || '').trim(),
        phone: String(data.get('phone') || '').trim(),
        email: String(data.get('email') || '').trim(),
        message: String(data.get('message') || '').trim(),
        topic: String(data.get('topic') || '').trim(),
        source: 'contact-form',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <section className="policy-hero">
        <div className="container">
          <p className="policy-crumbs">
            <Link to="/">Home</Link>
            <Icon name="chevron" size={14} style={{ transform: 'rotate(-90deg)' }} />
            <span>Contact</span>
          </p>
          <h1>Talk to a Security Specialist</h1>
          <p>
            Pricing questions, installation help, or renewals — reach us however suits you best.
          </p>
        </div>
      </section>

      <section className="policy-body">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-channels">
              {channels.map((channel) => {
                const inner = (
                  <>
                    <span className="contact-channel__icon">
                      <Icon name={channel.icon} size={22} />
                    </span>
                    <div>
                      <h3>{channel.title}</h3>
                      <p className="contact-channel__value">{channel.value}</p>
                      {channel.address && (
                        <CompanyAddress
                          showCompany={false}
                          className="contact-channel__address"
                        />
                      )}
                      <p className="contact-channel__note">{channel.note}</p>
                    </div>
                  </>
                );

                return channel.href ? (
                  <a key={channel.title} className="contact-channel" href={channel.href}>
                    {inner}
                  </a>
                ) : (
                  <div key={channel.title} className="contact-channel">
                    {inner}
                  </div>
                );
              })}

              <div className="policy-note" style={{ marginTop: 4 }}>
                <p>
                  <strong>Heads up:</strong> our specialists will never ask for your banking
                  password or a one-time security code. If someone claiming to be from us does,
                  hang up and call the number listed on this page.
                </p>
              </div>

              <div className="contact-disclaimer">
                <p className="contact-disclaimer__head">Disclaimer</p>
                {FOOTER_DISCLAIMER.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="policy-card contact-card">
              {sent ? (
                <div className="contact-done">
                  <span className="contact-done__icon">
                    <Icon name="check" size={30} strokeWidth={2.6} />
                  </span>
                  <h2 style={{ borderLeft: 0, paddingLeft: 0, marginTop: 0 }}>Message received</h2>
                  <p>
                    Thanks for getting in touch. A specialist will reply shortly. If it is urgent,
                    call {SUPPORT_PHONE} and we will pick up straight away.
                  </p>
                  <button type="button" className="btn btn--red" onClick={() => setSent(false)}>
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <h2 style={{ borderLeft: 0, paddingLeft: 0, marginTop: 0 }}>Send us a message</h2>
                  <p style={{ marginBottom: 22 }}>
                    Tell us what you need and we will come back with a clear answer — no scripted
                    replies.
                  </p>

                  {error && (
                    <p
                      role="alert"
                      style={{
                        marginBottom: 16,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: '#fef2f2',
                        color: '#b91c1c',
                        fontSize: 14,
                      }}
                    >
                      {error}
                    </p>
                  )}

                  <div className="contact-form__row">
                    <label htmlFor="c-name">
                      Full name
                      <input
                        id="c-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="Jane Smith"
                        disabled={pending}
                      />
                    </label>

                    <label htmlFor="c-phone">
                      Phone number
                      <input
                        id="c-phone"
                        name="phone"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder="+1 555 000 0000"
                        disabled={pending}
                      />
                    </label>
                  </div>

                  <label htmlFor="c-email">
                    Email address
                    <input
                      id="c-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@email.com"
                      disabled={pending}
                    />
                  </label>

                  <label htmlFor="c-topic">
                    What can we help with?
                    <select id="c-topic" name="topic" defaultValue="pricing" disabled={pending}>
                      <option value="pricing">Pricing &amp; plans</option>
                      <option value="install">Installation or activation</option>
                      <option value="renewal">Renewal or cancellation</option>
                      <option value="refund">Refund request</option>
                      <option value="other">Something else</option>
                    </select>
                  </label>

                  <label htmlFor="c-message">
                    Message
                    <textarea
                      id="c-message"
                      name="message"
                      rows={5}
                      required
                      placeholder="Describe the issue, including your device type and any error message you see."
                      disabled={pending}
                    />
                  </label>

                  {/* Honeypot — leave empty; bots that fill it are ignored by Lead Desk */}
                  <input
                    type="text"
                    name="_honey"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                  />

                  <button
                    type="submit"
                    className="btn btn--red btn--block btn--lg"
                    disabled={pending}
                  >
                    {pending ? 'Sending…' : 'Send Message'}
                  </button>
                  <p className="contact-form__fine">
                    By submitting this form you agree to our{' '}
                    <Link to="/privacy-policy">Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
